import React, { useState, useCallback } from "react"
import { observer } from "mobx-react"
import styled from "styled-components"

import useInject from "~/hooks/useInject"
import Input from "~/components/Form/LandingInput"
import FormError from "~/components/Form/ErrorText"
import WinItButton from "~/components/WinItButton"
import Spacer from "~/components/Spacer"

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 9px 24px rgba(0, 0, 0, 0.1);
  padding: 20px;
`

const Title = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  color: ${(props) => props.theme.colors.black};
  font-size: 1.25rem;
`

const SubText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  color: ${(props) => props.theme.colors.text.subContent};
  font-size: 0.95rem;
`

const InputRow = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "column" : "row")};
  gap: 12px;
  align-items: flex-start;
`

const Dismiss = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.text.subContent};
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
`

const mapStore = (store) => ({
  isMobile: store.ui.isMobile,
  licenseID: store.trafficLookup.licenseID,
  changeLicenseIDNumber: store.trafficLookup.changeLicenseIDNumber,
  validateDisplayMessage: store.trafficLookup.validateDisplayMessage,
  setFetcherName: store.trafficLookup.setFetcherName,
  setFetcherState: store.trafficLookup.setFetcherState,
  savePartialSearchCriteria: store.trafficLookup.savePartialSearchCriteria,
})

// Small, dismissible follow-up shown after a parking-ticket payment: asks
// only for the driver's license ID (everything else we already know about
// the user) and fires the same getFetcherTickets socket call the traffic
// lookup flow uses, so the backend can save a FetcherSearchCriteria for NY.
const LicensePopup = observer(() => {
  const {
    isMobile,
    licenseID,
    changeLicenseIDNumber,
    validateDisplayMessage,
    setFetcherName,
    setFetcherState,
    savePartialSearchCriteria,
  } = useInject(mapStore)
  const [dismissed, setDismissed] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      if (!licenseID || licenseID.trim().length < 3) return
      setFetcherName("NY")
      setFetcherState("NY")
      savePartialSearchCriteria("parking-payment-success")
      setSubmitted(true)
    },
    [licenseID, setFetcherName, setFetcherState, savePartialSearchCriteria]
  )

  if (dismissed) return null

  if (submitted) {
    return (
      <Card>
        <Title>Thanks!</Title>
        <SubText>
          We&apos;ll check your license for other outstanding New York
          traffic tickets.
        </SubText>
      </Card>
    )
  }

  const error = validateDisplayMessage("licenseID")

  return (
    <Card as="form" onSubmit={handleSubmit}>
      <Dismiss type="button" onClick={() => setDismissed(true)}>
        No thanks
      </Dismiss>
      <Title>Check for other NY traffic tickets</Title>
      <Spacer mobile={8} desktop={8} />
      <SubText>
        Enter your driver&apos;s license ID and we&apos;ll check for any
        other open tickets under your name.
      </SubText>
      <Spacer mobile={16} desktop={16} />
      <InputRow isMobile={isMobile}>
        <Input
          placeholder="License ID"
          value={licenseID}
          onChange={(e) => changeLicenseIDNumber(e.target.value)}
          error={error}
          maxLength={9}
          type="tel"
        />
        <WinItButton
          height={70}
          width={isMobile ? "100%" : "170px"}
          style={{ fontSize: "1rem" }}
          type="submit"
        >
          Check Now
        </WinItButton>
      </InputRow>
      <FormError error={error} />
    </Card>
  )
})

export default LicensePopup
